// The arena head block (D-V1; territory-visuals.md §4): a rounded square rotating with the
// heading, drawn as a base at ground level, then the lifted top (a side-shade rim around the main
// colour face). Shared by the renderer (every head) and the skin picker's previews.

import type { Ctx2D } from '../render/canvas.js';
import { css } from '../render/theme.js';
import type { ArenaShades } from './shades.js';

/** Corner radius as a share of the side. */
export const BLOCK_RADIUS = 0.18;
/** The top face's side as a share of the block's (the rest is the 10 % rim each side). */
export const BLOCK_FACE = 0.8;

/** A rounded square path of side `side` centred at (x, y), rotated so a side faces `heading`. */
export function roundedBlock(
  ctx: Ctx2D,
  x: number,
  y: number,
  side: number,
  heading: number,
  radius: number = BLOCK_RADIUS,
): void {
  const h = side / 2;
  const r = side * radius;
  const c = Math.cos(heading);
  const sn = Math.sin(heading);
  const P = (px: number, py: number): [number, number] => [
    x + px * c - py * sn,
    y + px * sn + py * c,
  ];
  ctx.beginPath();
  const pts: [number, number][] = [
    P(-h + r, -h),
    P(h - r, -h),
    P(h, -h),
    P(h, -h + r),
    P(h, h - r),
    P(h, h),
    P(h - r, h),
    P(-h + r, h),
    P(-h, h),
    P(-h, h - r),
    P(-h, -h + r),
    P(-h, -h),
  ];
  const [sx, sy] = pts[0] as [number, number];
  ctx.moveTo(sx, sy);
  for (let i = 0; i < 4; i++) {
    const a = pts[i * 3 + 1] as [number, number];
    const corner = pts[(i * 3 + 2) % 12] as [number, number];
    const b = pts[(i * 3 + 3) % 12] as [number, number];
    ctx.lineTo(a[0], a[1]);
    ctx.quadraticCurveTo(corner[0], corner[1], b[0], b[1]);
  }
  ctx.closePath();
}

/**
 * The block: base at (x, y), the top lifted `lift` px with its rim and face. Returns the face's
 * centre y (x is unchanged) for a skin decoration.
 */
export function drawBlock(
  ctx: Ctx2D,
  x: number,
  y: number,
  side: number,
  lift: number,
  heading: number,
  sh: ArenaShades,
): number {
  roundedBlock(ctx, x, y, side, heading);
  ctx.fillStyle = css(sh.base);
  ctx.fill();
  roundedBlock(ctx, x, y - lift, side, heading);
  ctx.fillStyle = css(sh.side);
  ctx.fill();
  roundedBlock(ctx, x, y - lift, side * BLOCK_FACE, heading);
  ctx.fillStyle = css(sh.main);
  ctx.fill();
  return y - lift;
}
